import { createContext, useCallback, useMemo, useState } from 'react';
import { useFetchData } from '../hooks/useFetchData';
import { useClientInfo } from '../hooks/useClientInfo';
import { Map as ImmutableMap } from 'immutable';
import events from 'customer-data-filters-ui/events.yaml';
import SegmentationFiltersErrorBoundary from '../components/SegmentationFiltersErrorBoundary';
import { configureComponent } from '../fetcher/adapter/adapters/ReferenceResolverAdapter';
import { getUsages } from '../api/crmUsagesServiceApi';
import { useCachedOperation } from '../hooks/useCachedOperation';
import { jsx as _jsx } from "react/jsx-runtime";
const SegmentationFiltersSharedContext = /*#__PURE__*/createContext({});
const SegmentationFiltersSharedProviderBase = ({
  children,
  referenceResolversByDSAssetFamily,
  resolvers,
  strategy,
  trackerInstance
}) => {
  const {
    currencyCode,
    getIsUngated,
    hasLimit,
    hasScope,
    isUserInfoFetched,
    portalId,
    userId
  } = useClientInfo();
  const [data, setData] = useState(ImmutableMap());
  const updateData = useCallback(value => {
    setData(prevData => typeof value === 'function' ? value(prevData) : value);
  }, []);
  const getHasFilterFamilyAccess = useCallback(filterFamily => {
    return strategy.getHasFilterFamilyAccess(filterFamily, hasScope, getIsUngated);
  }, [hasScope, getIsUngated, strategy]);
  const getTrackerInstance = useCallback(() => {
    if (!trackerInstance) {
      return null;
    }
    const clonedTrackerInstance = trackerInstance.clone({
      events,
      preserveTrackerEvents: false,
      preserveTrackerProperties: false,
      trackerName: 'filters-fe'
    });
    return clonedTrackerInstance;
  }, [trackerInstance]);
  const fetchMore = useFetchData(referenceResolversByDSAssetFamily, updateData, getHasFilterFamilyAccess, strategy, getIsUngated);
  const getPropertyUsagesCached = useCachedOperation(getUsages);
  const props = useMemo(() => ({
    children,
    referenceResolversByDSAssetFamily,
    resolvers,
    strategy
  }), [children, referenceResolversByDSAssetFamily, resolvers, strategy]);
  const contextValue = useMemo(() => ({
    currencyCode,
    data,
    fetchMore,
    getIsUngated,
    getTrackerInstance,
    hasLimit,
    hasScope,
    isUserInfoFetched,
    portalId,
    props,
    userId,
    getPropertyUsagesCached
  }), [currencyCode, data, fetchMore, getIsUngated, getTrackerInstance, hasLimit, hasScope, isUserInfoFetched, portalId, props, userId, getPropertyUsagesCached]);
  return /*#__PURE__*/_jsx(SegmentationFiltersSharedContext.Provider, {
    value: contextValue,
    children: children
  });
};
const SegmentationFiltersSharedProviderBaseWithAdapter = configureComponent(SegmentationFiltersSharedProviderBase);

// eslint-disable-next-line react/no-multi-comp
export const SegmentationFiltersSharedProvider = ({
  children,
  strategy,
  trackerInstance
}) => {
  return /*#__PURE__*/_jsx(SegmentationFiltersErrorBoundary, {
    children: /*#__PURE__*/_jsx(SegmentationFiltersSharedProviderBaseWithAdapter, {
      strategy: strategy,
      trackerInstance: trackerInstance,
      children: children
    })
  });
};
export default SegmentationFiltersSharedContext;